import React, { useState, useRef, useEffect } from "react";
import { ArrowRight, Check } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { useCmsBlocks } from "../lib/useCmsBlocks";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const resolveMedia = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);
const toWebm = (u) => (u ? u.replace(/\.mp4(\?.*)?$/i, ".webm$1") : u);

/* De fyra vanligaste eventtyperna. Idn är desamma som i platformConstants, så
   att /boka känner igen dem och fyller i fältet. En femte chip hade gjort raden
   till en kategorilista, och det är inte vad startsidan ska vara. */
const QUICK_TYPES = [
  { id: "wedding", sv: "Bröllop", en: "Wedding" },
  { id: "corporate_event", sv: "Företagsevent", en: "Corporate event" },
  { id: "private_party", sv: "Privatfest", en: "Private party" },
  { id: "birthday_party", sv: "Födelsedag", en: "Birthday" },
];

const Hero = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [free, setFree] = useState("");
  const submitFree = (e) => {
    e.preventDefault();
    const q = free.trim();
    navigate(q ? `/boka?prompt=${encodeURIComponent(q)}` : "/boka");
  };
  const cmsBlocks = useCmsBlocks();
  const bgUrl = resolveMedia(cmsBlocks["home.hero.background_url"]);
  const posterUrl = resolveMedia(cmsBlocks["home.hero.poster_url"] || "");
  const overlayOpacity = cmsBlocks["home.hero.overlay_opacity"] || "0.5";
  const isVideo = bgUrl && /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(bgUrl);
  const isMp4 = bgUrl && /\.mp4(\?.*)?$/i.test(bgUrl);
  const [videoFailed, setVideoFailed] = useState(false);
  const videoRef = useRef(null);
  const showVideo = isVideo && !videoFailed;
  // Static image background, or poster fallback when every video source truly fails.
  const imageBg = isVideo ? (videoFailed ? posterUrl : null) : (bgUrl || posterUrl) || null;

  useEffect(() => { setVideoFailed(false); }, [bgUrl]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !showVideo) return;
    v.load();
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  }, [bgUrl, showVideo]);

  // Fires only after all <source> candidates fail. Fall back to the poster image.
  const handleVideoError = () => setVideoFailed(true);

  return (
    <section className="relative overflow-hidden pt-[68px]">
      {showVideo && (
        <video ref={videoRef} autoPlay muted loop playsInline preload="auto" poster={posterUrl || undefined} onError={handleVideoError} data-testid="hero-video"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 0 }}>
          {isMp4 && <source src={toWebm(bgUrl)} type="video/webm" />}
          <source src={bgUrl} type={isMp4 ? "video/mp4" : undefined} />
        </video>
      )}
      {imageBg && (
        <div data-testid="hero-image-bg" style={{ position: "absolute", inset: 0, backgroundImage: `url(${imageBg})`, backgroundSize: "cover", backgroundPosition: "center", zIndex: 0 }} />
      )}
      {(showVideo || imageBg) && <div style={{ position: "absolute", inset: 0, backgroundColor: "#000000", opacity: parseFloat(overlayOpacity), zIndex: 1 }} data-testid="hero-overlay" />}
      <div className="pointer-events-none absolute top-[120px] left-1/2 -translate-x-1/2 w-[900px] h-[560px] rounded-full animate-glow"
        style={{ background: "radial-gradient(ellipse at center, rgba(255,92,0,0.16) 0%, rgba(255,92,0,0.04) 40%, transparent 70%)" }}
      />
      <div className="relative max-w-[1200px] mx-auto px-6" style={{ zIndex: 2 }}>
        <div className="min-h-[560px] grid md:grid-cols-2 gap-10 lg:gap-14 items-center py-20">

          {/* Vänster: löftet. Rubriken säger vad vi gör, inte vad kunden slipper.
              Den gamla lydelsen "Sluta scrolla" beskrev problemet hos någon
              annan. Den här beskriver vad som händer här. */}
          <div>
            <h1 className="animate-fade-up text-[40px] sm:text-[54px] md:text-[64px] leading-[1.03] font-extrabold tracking-tight text-white">
              {t("hero.title")}<br />
              <span className="text-[#FF5C00]">{t("hero.titleAccent")}</span>
            </h1>
            <p className="animate-fade-up max-w-[520px] mt-6 text-[17px] md:text-[18px] leading-relaxed text-neutral-300" style={{ animationDelay: "0.1s" }}>
              {t("hero.subtitle")}
            </p>
            {/* Tre löften vi faktiskt håller. Det tredje är inget de andra kan
                säga: en förfrågan här ger högst fem svar, inte en inkorg full. */}
            <div className="animate-fade-up flex flex-wrap items-center gap-x-5 gap-y-2 mt-8 text-[13px] text-neutral-400" style={{ animationDelay: "0.15s" }}>
              {[t("hero.trust1"), t("hero.trust2"), t("hero.trust3")].map((x) => (
                <span key={x} className="flex items-center gap-1.5">
                  <Check size={14} className="text-[#1A8A4A]" /> {x}
                </span>
              ))}
            </div>
          </div>

          {/* Höger: ingången. Panelen är ett formulär och utger sig inte för att
              vara något annat. Designunderlaget ritade den som en chattassistent
              med grön punkt och "Online". Ingen assistent svarar, och en yta som
              låtsas konversera bränner förtroendet i den första sekunden av
              relationen. Chipsen och fritexten skickas till /boka och fyller i
              formuläret i förväg, vilket är hela nyttan utan låtsandet. */}
          <div className="animate-fade-up rounded-2xl border border-white/10 bg-[#0F0F0F]/85 backdrop-blur-xl p-6"
            style={{ animationDelay: "0.2s" }} data-testid="hero-panel">
            <div className="text-white font-semibold text-[17px]">{t("hero.panelTitle")}</div>
            <p className="text-[13px] text-neutral-400 mt-1">{t("hero.panelHint")}</p>

            <div className="flex flex-wrap gap-2 mt-5">
              {QUICK_TYPES.map((q) => (
                <Link key={q.id} to={`/boka?event_type=${q.id}`} data-testid={`hero-chip-${q.id}`}
                  className="px-3.5 py-2 rounded-full text-[13px] font-medium border border-[#FF5C00]/30 bg-[#FF5C00]/8 text-[#FF7A2F] hover:bg-[#FF5C00]/18 transition-colors">
                  {lang === "sv" ? q.sv : q.en}
                </Link>
              ))}
            </div>

            <form onSubmit={submitFree} className="relative mt-4" data-testid="hero-free-form">
              <input value={free} onChange={(e) => setFree(e.target.value)} maxLength={2000}
                placeholder={t("hero.freeText")} data-testid="hero-free-input"
                className="w-full h-[52px] pl-4 pr-14 rounded-xl bg-[#1A1A1A] border border-[#2A2A2A] text-[#F5F3EE] text-[15px] placeholder-neutral-600 focus:outline-none focus:border-[#FF5C00] transition-colors" />
              <button type="submit" aria-label={t("hero.post")} data-testid="hero-free-submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-lg bg-[#1A8A4A] hover:bg-[#20A458] flex items-center justify-center transition-colors">
                <ArrowRight size={18} className="text-white" />
              </button>
            </form>

            <div className="mt-3.5 text-[11.5px] text-neutral-500 text-center">{t("hero.example")}</div>

            <div className="mt-5 pt-4 border-t border-white/10 text-center">
              <Link to="/signin?role=talent" data-testid="hero-talent-link"
                className="text-[13px] text-neutral-400 hover:text-white transition-colors">
                {t("hero.join")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
